import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { GuestManager } from "@/components/ui/guest-manager";
import { StepProps } from "@/lib/builder/models/builder.model";
import ImageUpload from "../../components/ui/ImageUpload";
import { IGuest } from "../request-trip/models/travel-request.model";

const ItineraryStep = ({
  data,
  onDataChange,
  canProceed,
  setCanProceed,
  isReadOnly = false
}: StepProps) => {
  const [localData, setLocalData] = useState(data);

  const handleInputChange = (field: keyof typeof localData, value: string | number) => {
    const newData = localData.update({ [field]: value });
    setLocalData(newData);
    onDataChange(newData);
  };

  const handleImageChange = (newImage: string | null) => {
    const newData = localData.update({ image: newImage });
    setLocalData(newData);
    onDataChange(newData);
  };

  // Guest management functions
  const addGuest = () => {
    const newGuest: IGuest = { id: Date.now(), age: 25 };
    const newGuestsData = [...(localData.guests_data || []), newGuest];
    const newData = localData.update({ guests_data: newGuestsData });
    setLocalData(newData);
    onDataChange(newData);
  };

  const removeGuest = () => {
    if ((localData.guests_data || []).length <= 1) return;

    const newGuestsData = (localData.guests_data || []).slice(0, -1);
    const newData = localData.update({ guests_data: newGuestsData });
    setLocalData(newData);
    onDataChange(newData);
  };

  const updateGuestAge = (guestId: number, newAge: number) => {
    const validAge = Math.max(0, newAge);
    const newGuestsData = (localData.guests_data || []).map(guest =>
      guest.id === guestId ? { ...guest, age: validAge } : guest
    );
    const newData = localData.update({ guests_data: newGuestsData });
    setLocalData(newData);
    onDataChange(newData);
  };

  useEffect(() => {
    // Check if we can proceed (basic validation)
    const canProceedNow = localData.title.trim() !== '' &&
      localData.start_date !== '' &&
      localData.end_date !== '';
    setCanProceed(canProceedNow);
  }, [localData, setCanProceed]);


  return (
    <div className="space-y-8 px-16 py-8">

      {/* Image Upload Section */}
      <div className="space-y-4">
        <Label className="text-lg font-medium">General Informations</Label>
        <ImageUpload
          image={localData.image || null}
          onImageChange={handleImageChange}
          maxSizeMB={10}
          disabled={isReadOnly}
        />
      </div>

      {/* Trip Title */}
      <div className="space-y-2">
        <Input
          id="title"
          value={localData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="Enter your trip title"
          className="text-lg"
          disabled={isReadOnly}
        />
      </div>

      {/* Dates */}
      <div className={`space-y-2`}>
        <Label className="text-lg font-medium">
          Dates
        </Label>
        <DateRangePicker
          startDate={localData.start_date}
          endDate={localData.end_date}
          onStartDateChange={(date) => handleInputChange('start_date', date)}
          onEndDateChange={(date) => handleInputChange('end_date', date)}
          startDatePlaceholder="From"
          endDatePlaceholder="To"
          disabled={isReadOnly}
        />
      </div>

      {/* Guest Management */}
      <div className="space-y-2">
        <Label htmlFor="guests" className="text-lg font-medium">
          Guests
        </Label>
        <GuestManager
          totalGuests={localData.total_guests}
          guests={localData.guests_data || []}
          onAddGuest={addGuest}
          onRemoveGuest={removeGuest}
          onUpdateGuestAge={updateGuestAge}
          disabled={isReadOnly}
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description" className="text-lg font-medium">
          Trip description
        </Label>
        <Textarea
          id="description"
          value={localData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Enter a short overview"
          rows={4}
          className="resize-none"
          disabled={isReadOnly}
        />
      </div>
    </div>
  );
};

export default ItineraryStep;